import { Link } from 'react-router-dom'
import MetaTags from '../components/MetaTags'
import DinBilpartnerBadge from '../components/DinBilpartnerBadge'
import ReviewsSlider from '../components/ReviewsSlider'
import ContactForm from '../components/ContactForm'
import { imageUrl } from '../lib/images'

export default function DaekhotelPage({ settings }) {
  const bookingUrl = settings?.booking_url || 'https://service.dinbilpartner.dk/book-tid'
  const phone = settings?.phone || '22 18 73 30'
  const phoneRaw = settings?.phone_raw || '22187330'

  return (
    <>
      <MetaTags
        title="Dækhotel | Opbevaring af dæk nær Billund og Grindsted - EK Biler"
        description="Hos EK Biler tilbyder vi opbevaring af dine dæk på vores dækhotel. Placeret i Hejnsvig nær Billund og Grindsted passer vi godt på dine dæk | Læs mere"
        ogTitle="Dækhotel | Opbevaring af dæk nær Billund og Grindsted - EK Biler"
        ogDescription="Hos EK Biler tilbyder vi opbevaring af dine dæk på vores dækhotel. Placeret i Hejnsvig nær Billund og Grindsted passer vi godt på dine dæk | Læs mere"
        ogImage={imageUrl('shutterstock-2307555301-optimized-1.jpg', 'width=1200,quality=85,format=auto')}
      />

      {/* Hero Header */}
      <section className="bg-[#14171a] text-white py-14 sm:py-20 border-b border-[#262c33] relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src={imageUrl('shutterstock-2307555301-optimized-1.jpg', 'width=1600,quality=80,format=auto')}
            alt="Dækhotel hos EK Biler"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <Link to="/" className="hover:text-white">Forside</Link>
              <span>/</span>
              <span className="text-[#e20019]">Autoværksted</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Dækhotel
            </h1>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
              Professionel service og reparation og skader af alle bilmærker. Speciale i V.A.G
              Gruppen og altid mulighed for lånebil, når din bil er på værksted.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white px-6 py-3 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-red-500 text-center"
              >
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                <span>Book plads på dækhotel</span>
              </a>
              <a
                href={`tel:${phoneRaw}`}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#20252c] hover:bg-[#2b323c] border border-gray-700 text-white px-5 py-3 rounded-xl font-bold text-sm transition-colors whitespace-nowrap text-center"
              >
                <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
                <span>Ring på {phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Packages */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Verbatim Content */}
            <div className="lg:col-span-7 space-y-8 text-gray-800 leading-relaxed text-base">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  Dækhotel nær Billund og Grindsted
                </h2>
                <p>
                  Oplever du også, at dine dæk fylder mere, end du har plads til i garagen eller
                  kælderen? Hos EK Biler nær Billund og Grindsted tilbyder vores autoværksted
                  dækopbevaring, så du slipper for besværet! Et sæt bildæk kan tage overraskende meget
                  plads, og ved at lade os opbevare dine dæk, frigiver du plads til andre ting – og
                  samtidig sikrer du, at dine dæk opbevares under de rette forhold. Vi hjælper bilejere
                  i Billund, Grindsted og omegn med at få styr på dækkene og skabe mere plads
                  derhjemme.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Fordele ved dækopbevaring</h2>
                <p>Der er mange gode grunde til at vælge dækopbevaring på vores dækhotel:</p>
                <div className="space-y-4 mt-4">
                  <div className="border-l-4 border-[#e20019] pl-4">
                    <strong className="block text-gray-900">Korrekte opbevaringsforhold:</strong>
                    <span className="text-sm text-gray-600">
                      Dækkene opbevares i et tørt og tempereret miljø, hvilket forlænger deres levetid.
                      Forkert opbevaring derhjemme kan resultere i sprækker og ujævn slitage, som
                      forkorter dækkenes holdbarhed.
                    </span>
                  </div>
                  <div className="border-l-4 border-[#e20019] pl-4">
                    <strong className="block text-gray-900">Nem adgang til dine dæk:</strong>
                    <span className="text-sm text-gray-600">
                      Når det er tid til hjulskifte, er dine dæk allerede på vores autoværksted nær
                      Billund og Grindsted – klar til montering.
                    </span>
                  </div>
                  <div className="border-l-4 border-[#e20019] pl-4">
                    <strong className="block text-gray-900">Ingen tung løft og opbevaringsbesvær:</strong>
                    <span className="text-sm text-gray-600">
                      Du slipper for selv at slæbe tunge hjul frem og tilbage – og vi sørger for, at de
                      er klar til brug, når du har brug for dem.
                    </span>
                  </div>
                </div>
                <p className="mt-4 text-sm text-gray-700">
                  Hvis du vælger et dækhotel, får du ikke bare opbevaret dine dæk, men også
                  professionel behandling med i prisen! Det er din garanti for, at dækkene holder
                  længere og yder optimalt.
                </p>
                <p className="mt-2 text-sm text-gray-700">
                  Vi kan også hjælpe dig med{' '}
                  <Link to="/elbil-service" className="text-[#e20019] font-bold hover:underline">
                    elbil service
                  </Link>{' '}
                  så din bil for optimal drift og længere levetid.
                </p>
              </div>

              {/* Photo break */}
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                <img
                  src={imageUrl('shutterstock-2307555301-optimized-1.jpg', 'width=800,quality=80,format=auto')}
                  alt="Dækhotel opbevaring"
                  className="w-full h-auto object-cover"
                />
              </div>

              {/* Packages from Scrape */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Vælg en pakkeløsning til dine behov!
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  Hos EK Biler tilbyder vi tre forskellige pakkeløsninger, så du kan finde den, der
                  passer bedst til dine behov:
                </p>

                <div className="grid grid-cols-1 gap-4">
                  {/* Package 1 */}
                  <div className="p-5 rounded-2xl bg-gray-50 border border-gray-200 hover:border-gray-300 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200 pb-3 mb-3">
                      <h3 className="font-bold text-lg text-gray-900">Pakkeløsning 1</h3>
                      <div className="text-xl font-extrabold text-[#e20019]">
                        595 kr. <span className="text-xs text-gray-500 font-normal">pr. halv sæson</span>
                      </div>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-700">
                      <li className="flex items-center gap-2">
                        <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                        <span>Hjulskift</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                        <span>Dækopbevaring</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                        <span>Kontrol og justering af dæktryk</span>
                      </li>
                    </ul>
                  </div>

                  {/* Package 2 */}
                  <div className="p-5 rounded-2xl bg-gray-50 border border-gray-200 hover:border-gray-300 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200 pb-3 mb-3">
                      <div>
                        <h3 className="font-bold text-lg text-gray-900">Pakkeløsning 2</h3>
                        <div className="text-xs text-gray-500 font-medium">(EL – Bil eller Varebil)</div>
                      </div>
                      <div className="text-xl font-extrabold text-[#e20019]">
                        695 kr. <span className="text-xs text-gray-500 font-normal">pr. halv sæson</span>
                      </div>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-700">
                      <li className="flex items-center gap-2">
                        <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                        <span>Hjulskift</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                        <span>Dækopbevaring</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                        <span>Kontrol og justering af dæktryk</span>
                      </li>
                    </ul>
                  </div>

                  {/* Package 3 */}
                  <div className="p-5 rounded-2xl bg-gray-50 border-2 border-red-200 relative hover:border-red-300 transition-colors">
                    <div className="absolute top-3 right-3 text-[10px] uppercase font-bold bg-[#e20019] text-white px-2 py-0.5 rounded">
                      Populær
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200 pb-3 mb-3">
                      <div>
                        <h3 className="font-bold text-lg text-gray-900">Pakkeløsning 3</h3>
                        <div className="text-xs text-gray-500 font-medium">
                          (Tillæg på 100 kr for EL – Bil)
                        </div>
                      </div>
                      <div className="text-xl font-extrabold text-[#e20019]">
                        995 kr. <span className="text-xs text-gray-500 font-normal">pr. halv sæson</span>
                      </div>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-700">
                      <li className="flex items-center gap-2">
                        <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                        <span>Hjulskift</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                        <span>Dækopbevaring</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                        <span>Afbalancering</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                        <span>Kontrol og justering af dæktryk</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="fa-solid fa-check text-emerald-600 text-xs" aria-hidden="true" />
                        <span>Vask af hjul</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <p className="mt-4 text-xs text-gray-500 italic">
                  Alle vores pakkeløsninger inkluderer både dækopbevaring og{' '}
                  <Link to="/daekskift" className="text-[#e20019] font-bold underline">
                    dækskift
                  </Link>{' '}
                  hver halve sæson, så du får en samlet service uden bekymringer.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Vi passer gerne på dine dæk – kontakt os!
                </h3>
                <p>
                  Vil du høre mere om vores dækhotel? Hos EK Biler står vi klar til at besvare dine
                  spørgsmål og hjælpe dig med at vælge den løsning, der passer bedst til dig og din bil.
                  Uanset om du bor i Billund, Grindsted eller omegn, kan du trygt overlade dine dæk til
                  os. Ring til os eller kig forbi værkstedet, så finder vi en løsning til dig. Hos os er
                  du sikker på, at dine dæk er i de bedste hænder!
                </p>
              </div>
            </div>

            {/* Right Column: Sticky Booking Card & Contact Form */}
            <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24">
              <div className="bg-[#14171a] text-white p-6 sm:p-8 rounded-2xl border border-gray-800 space-y-4">
                <div className="text-xs font-bold text-[#e20019] uppercase tracking-wider">
                  Dækhotel i Hejnsvig
                </div>
                <h3 className="text-2xl font-bold">Book dækopbevaring</h3>
                <p className="text-sm text-gray-300">
                  Reserver plads på dækhotellet i dag eller book tid til sæsonskifte.
                </p>
                <div className="pt-2">
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full text-center inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white py-3.5 px-6 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                    <span>Book tid til dækhotel</span>
                  </a>
                </div>
              </div>

              <ContactForm
                title="Send en forespørgsel"
                subtitle="Vi vender tilbage for at fuldføre din booking"
                bookingUrl={bookingUrl}
              />
            </div>
          </div>
        </div>
      </section>

      <DinBilpartnerBadge />
      <ReviewsSlider />
    </>
  )
}
